import { MAX_COMPRESS_IMAGE_SIZE } from '../config/dataConfig/FJGenerateData';

// 计算 base64 编码图片大小
const getBase64ImageSize = base64 => {
    const indexBase64 = base64.indexOf('base64,');
    if (indexBase64 < 0) return -1;
    const str = base64.substr(indexBase64 + 6);
    // 大小单位：字节
    return (str.length * 0.75).toFixed(2);
};

const FJCompressedImageStore = {
    /**
     * 	图像压缩，默认同比例压缩
     * @param {Object} imgPath
     *	图像 base64 编码字符串或图像可访问路径
     * @param {Object} compParamObj
     *	压缩参数，compParamObj 对象的属性：width-宽，height-高，
     *  ratio(0-1)-宽高压缩率，quality(0-1)-图像质量系数
     * @param {Object} maxSize
     *	指定压缩后的文件大小，单位：字节
     * @param {Object} callback
     *	回调函数有一个参数，base64 的字符串数据
     * @param {Object} left
     *	二分递归查找 left
     * @param {Object} right
     *	二分递归查找 right
     * @param {Object} oQuality
     *	初始图像质量系数
     */
    compressedImage(imgPath, compParamObj, maxSize, callback, left = 0, right = null, oQuality = null) {
        const _this = this;
        let img = new Image();
        img.src = imgPath;

        img.onload = function () {
            const _thisImg = this;
            // 默认按比例压缩
            let w = _thisImg.width * (compParamObj?.ratio || 1),
                h = _thisImg.height * (compParamObj?.ratio || 1),
                scale = w / h;
            w = compParamObj.width || w;
            h = (compParamObj.height && compParamObj.height * (w / scale)) || h;
            // 生成 canvas
            let canvas = document.createElement('canvas');
            let ctx = canvas.getContext('2d');
            canvas.width = w;
            canvas.height = h;
            ctx.drawImage(_thisImg, 0, 0, w, h);
            // 图像质量系数，默认图片质量为 0.8
            let quality = 0.8;
            if (compParamObj.quality && compParamObj.quality > 0 && compParamObj.quality <= 1) {
                quality = compParamObj.quality;
            }
            // quality 值越小，所绘制出的图像越模糊
            let newBase64Image = canvas.toDataURL('image/jpeg', quality);
            let fileSize = getBase64ImageSize(newBase64Image);

            if (left <= right) {
                if (oQuality === null) {
                    oQuality = quality;
                }

                const qualityArr = [],
                    step = 0.01;
                for (let i = step; i <= oQuality; i += step) {
                    qualityArr.push(parseFloat(i));
                }

                if (right === null) {
                    right = qualityArr.length;
                }

                const mid = Math.floor((left + right) / 2);

                if (fileSize > maxSize) {
                    _this.compressedImage(
                        imgPath,
                        {
                            quality: qualityArr[mid],
                            // 设置宽高压缩率对图像进行宽高压缩，降低图像被压缩后出现彩虹斑的情况
                            ratio: 0.5,
                        },
                        maxSize,
                        callback,
                        left,
                        mid - 1,
                        oQuality,
                    );
                } else {
                    _this.compressedImage(
                        imgPath,
                        {
                            quality: qualityArr[mid],
                            // 设置宽高压缩率对图像进行宽高压缩，降低图像被压缩后出现彩虹斑的情况
                            ratio: 0.5,
                        },
                        maxSize,
                        callback,
                        mid + 1,
                        right,
                        oQuality,
                    );
                }
                return;
            }
            if (callback) {
                // 回调函数返回压缩后的 base64 图像
                canvas.toBlob(blob => {
                    callback(blob);
                });
            }
        };
    },
    compressedImageAsync: function (imgPath, compParamObj = {}, maxSize) {
        return new Promise(res => {
            this.compressedImage(imgPath, compParamObj, maxSize, blob => {
                res(blob);
            });
        });
    },

    simpleCompressImage: function (imgPath, maxWidth = MAX_COMPRESS_IMAGE_SIZE, maxHeight = MAX_COMPRESS_IMAGE_SIZE) {
        return new Promise(res => {
            let img = new Image();
            img.src = imgPath;
            img.onload = function () {
                let canvas = document.createElement('canvas');
                let ctx = canvas.getContext('2d');
                let width = img.width;
                let height = img.height;
                if (width > height) {
                    if (maxWidth && width > maxWidth) {
                        height *= maxWidth / width;
                        width = maxWidth;
                    }
                } else {
                    if (maxHeight && height > maxHeight) {
                        width *= maxHeight / height;
                        height = maxHeight;
                    }
                }
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob(blob => {
                    res(blob);
                });
            };
            img.onerror = () => {
                res();
            };
        });
    },
    compressImageByFile: function (file, options = {}) {
        return new Promise(res => {
            const {
                outPutImageType = 'image/jpg',
                maxPixel = MAX_COMPRESS_IMAGE_SIZE,
                returnType = 'object',
            } = options;
            const reader = new FileReader();
            reader.onload = function (e) {
                const img = new Image();
                img.onload = function () {
                    let canvas = document.createElement('canvas');
                    let ctx = canvas.getContext('2d');
                    let width = img.width;
                    let height = img.height;
                    if (width > height) {
                        if (maxPixel && width > maxPixel) {
                            height *= maxPixel / width;
                            width = maxPixel;
                        }
                    } else {
                        if (maxPixel && height > maxPixel) {
                            width *= maxPixel / height;
                            height = maxPixel;
                        }
                    }
                    width = Math.floor(width);
                    height = Math.floor(height);
                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);
                    const src = canvas.toDataURL(outPutImageType);
                    if (returnType === 'object') {
                        res({
                            width,
                            height,
                            src,
                            name: file.name,
                            id: new Date().getTime(),
                        });
                    } else if (returnType === 'url') {
                        res(src);
                    } else {
                        canvas.toBlob(blob => {
                            res(blob);
                        });
                    }
                };
                img.onerror = () => {
                    res();
                };
                img.src = e.target.result;
            };
            reader.onerror = () => {
                res();
            };
            reader.readAsDataURL(file);
        });
    },
    compressImageByUrl: function (url, options = {}) {
        return new Promise(resolve => {
            const {
                outPutImageType = 'image/jpg',
                maxPixel = MAX_COMPRESS_IMAGE_SIZE,
                returnType = 'object',
                name,
            } = options;
            const img = new Image();
            img.onload = function () {
                let canvas = document.createElement('canvas');
                let ctx = canvas.getContext('2d');
                let width = img.width;
                let height = img.height;
                if (width > height) {
                    if (maxPixel && width > maxPixel) {
                        height *= maxPixel / width;
                        width = maxPixel;
                    }
                } else {
                    if (maxPixel && height > maxPixel) {
                        width *= maxPixel / height;
                        height = maxPixel;
                    }
                }
                width = Math.floor(width);
                height = Math.floor(height);
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);
                const src = canvas.toDataURL(outPutImageType);
                if (returnType === 'object') {
                    resolve({
                        width,
                        height,
                        src,
                        name: name,
                        id: new Date().getTime(),
                    });
                } else if (returnType === 'url') {
                    resolve(src);
                } else {
                    canvas.toBlob(blob => {
                        resolve(blob);
                    });
                }
            };
            img.onerror = () => {
                resolve();
            };
            img.src = url;
        });
    },
};
export default FJCompressedImageStore;
